/* @flow */

import React from 'react'
import { connect } from 'react-redux'

import type {
  Design,
  Customer,
  CustomerId,
  CheckedoutPoll,
  Vector,
  Tag,
} from '../../types'
import { setSelectedExpertOpinion } from '../../stores/pollBuilderSelectedExpertOpinion'
import { openSnippetEditor } from '../../stores/pollBuilderIconPicker'
import { getTags } from '../../stores/tags'
import { newMinLengthValidator, newMaxLengthValidator } from '../../helpers'

import CustomerSelect from '../CustomerSelect/'
import Controls from './Controls'
import OpinaryInput from '../Controls/OpinaryInput'
import IfAdmin from '../IfAdmin'
import PollFormToggles from './PollFormToggles'
import Condition from '../Condition'
import { MIN_POLL_LABEL_LENGTH, MAX_POLL_LABEL_LENGTH } from '../CheckedoutPoll'
import PollSketch from '../PollSketch'
import TagsSelect from './TagsSelect'

function TwoAxisPollForm(props: {
  poll: CheckedoutPoll,
  tags: Array<Tag>,
  updatePoll: Object => void,
  designs: Array<Design>,
  customers: Array<Customer>,
  disabled?: boolean,
  isAdmin: boolean,
  customerIdOfCurrentUser?: CustomerId,

  expertOpinionCreateHelper: ?Vector,
  setExpertOpinionCreateHelper: (position: ?Vector) => void,
  moveCheckedoutExpertOpinionWhereIndex: (
    index: number,
    position: Vector
  ) => void,
  setPositionOfNewExpertOpinion: (position: ?Vector) => void,
  setSelectedExpertOpinion: Object => void,
  openIconPicker: () => void,
  closeIconPicker: () => void,
  openSnippetEditor: () => void,
}) {
  const { disabled, poll, tags, isAdmin } = props

  function setPollAttribute(key, value) {
    props.updatePoll({
      ...props.poll,
      [key]: value,
    })
  }

  function makeChangeHandler(key: string) {
    return (event: Event) => {
      if (event.target.value == null) {
        throw new Error('expected event.target.value to exist')
      }
      props.updatePoll({
        ...props.poll,
        [key]: event.target.value,
      })
    }
  }

  const minError = newMinLengthValidator(MIN_POLL_LABEL_LENGTH)
  const maxError = newMaxLengthValidator(MAX_POLL_LABEL_LENGTH)

  const isFormValid = () => {
    return (
      minError(poll.header) == null &&
      maxError(poll.header) == null &&
      maxError(poll.labelLeft) == null &&
      maxError(poll.labelRight) == null &&
      maxError(poll.labelTop) == null &&
      maxError(poll.labelBottom) == null &&
      (minError(poll.labelLeft) == null ||
        minError(poll.labelRight) == null ||
        minError(poll.labelBottom) == null ||
        minError(poll.labelTop) == null)
    )
  }

  function updateName(event) {
    const { value } = event.target
    let userEnteredId = true
    if (!value) {
      userEnteredId = false
    }

    props.updatePoll({
      ...props.poll,
      name: event.target.value,
      userEnteredId,
    })
  }

  function setCustomerId(id) {
    props.updatePoll({
      ...props.poll,
      customer: id,
    })
  }

  const handlerBlur = event => {
    let name = event.target.name

    event.target.name && event.target.value
      ? props.updatePoll({ [name]: false })
      : props.updatePoll({ [name]: true })
  }

  const customerSelectValue = props.isAdmin
    ? props.poll.customer
    : props.customerIdOfCurrentUser

  const renderInput = (label, onChange, value, disabled) => {
    return (
      <div className="mb4">
        <label>{label}</label>
        <OpinaryInput
          onChange={onChange}
          value={value}
          className="w-100"
          borderClass={maxError(poll.header) ? 'b--dark-red' : 'b--moon-gray'}
          maxLength={MAX_POLL_LABEL_LENGTH + 1}
          disabled={disabled}
        />

        <Condition shouldRender={maxError(value) != null}>
          <div className="tl dark-red">{maxError(value)}</div>
        </Condition>
      </div>
    )
  }

  const handleIconClick = clickedIcon => {
    props.setSelectedExpertOpinion(clickedIcon)
    props.openSnippetEditor()
  }

  return (
    <form className="flex flex-column items-center">
      <div className="w-100 relative mb4 flex">
        <span className="bg-op-con-dark op-con-light f6 ph4 h3 flex items-center">
          <span className="nowrap">Compass</span>
        </span>
        <span className="w-60 mh4 mr-auto">
          <IfAdmin>
            <CustomerSelect
              className="w-100 bb b--moon-gray"
              colorClass="gray"
              onChange={setCustomerId}
              customerId={customerSelectValue}
            />
          </IfAdmin>
        </span>
        <span className="f6 bg-near-white gray ph4 h3 flex items-center">
          <span>Example</span>
        </span>
      </div>

      <div className="cf w-90 flex items-center mb4">
        <div className="w-50">
          <div className="mb4">
            <label>Question</label>
            <OpinaryInput
              onChange={makeChangeHandler('header')}
              value={poll.header}
              className="w-100"
              borderClass={
                maxError(poll.header) ? 'b--dark-red' : 'b--moon-gray'
              }
              maxLength={MAX_POLL_LABEL_LENGTH + 1}
              disabled={disabled}
              onBlur={handlerBlur}
              name="questionInput"
            />

            <Condition shouldRender={poll.questionInput ? true : false}>
              <div className="tl dark-red mt1"> Field cannot be empty </div>
            </Condition>
            <Condition shouldRender={maxError(poll.header) != null}>
              <div className="tl dark-red mt1">{maxError(poll.header)}</div>
            </Condition>
          </div>

          <div className="mb4">
            <label>Label Top</label>
            <OpinaryInput
              onChange={makeChangeHandler('labelTop')}
              value={poll.labelTop}
              className="w-100"
              borderClass={
                maxError(poll.labelTop) ? 'b--dark-red' : 'b--moon-gray'
              }
              maxLength={MAX_POLL_LABEL_LENGTH + 1}
              disabled={disabled}
              onBlur={handlerBlur}
              name="labelTopField"
            />

            <Condition shouldRender={poll.labelTopField ? true : false}>
              <div className="tl dark-red mt1"> Field cannot be empty </div>
            </Condition>
            <Condition shouldRender={maxError(poll.labelTop) != null}>
              <div className="tl dark-red mt1">{maxError(poll.labelTop)}</div>
            </Condition>
          </div>

          {renderInput(
            'Label Bottom',
            makeChangeHandler('labelBottom'),
            poll.labelBottom,
            disabled
          )}
          {renderInput(
            'Label Left',
            makeChangeHandler('labelLeft'),
            poll.labelLeft,
            disabled
          )}
          {renderInput(
            'Label Right',
            makeChangeHandler('labelRight'),
            poll.labelRight,
            disabled
          )}

          <label className="pt4 db">Poll ID</label>
          {poll.id ? (
            <span className="gray mt1">{poll.name}</span>
          ) : (
            <OpinaryInput id="name" onChange={updateName} value={poll.name} />
          )}

          <TagsSelect
            options={tags}
            className="w-100 mt4"
            value={poll.tags}
            onChange={val => setPollAttribute('tags', val)}
          />
        </div>

        <div className="w-50 tc">
          <PollSketch
            className="center-block"
            poll={poll}
            onIconClick={handleIconClick}
          />
        </div>
      </div>
      <div className="w-90 flex mb4">
        <PollFormToggles
          poll={poll}
          toggleAttribute={setPollAttribute}
          isAdmin={isAdmin}
        />
      </div>

      <div className="w-100 h3 bg-near-white flex items-center justify-center">
        <div className="w-90">
          <Controls poll={poll} isFormValid={isFormValid()} />
        </div>
      </div>
    </form>
  )
}

const mapStateToProps = state => {
  return {
    tags: getTags(state),
  }
}

const mapDispatchToProps = {
  setSelectedExpertOpinion,
  openSnippetEditor,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TwoAxisPollForm)



// WEBPACK FOOTER //
// ./src/components/PollForm/TwoAxesPollForm.js